import { useCallback, useEffect, useState } from "react";
import { getAppraisalFeatureFlags } from "./appraisalApi";

export const EMPTY_APPRAISAL_FEATURES = {
  isRetrospective: false,
  isFeedback: false,
  isBehaviorGroup: false,
  isKpi: false,
  isDevelopment: false,
  isOneToOne: false,
  isOneToOneConversation: false,
  isPayIncrease: false,
  isLms: false,
};

const STORAGE_KEY = "appraisalFeatureFlagsV1";
export const APPRAISAL_FEATURES_CHANGED_EVENT = "hrm-appraisal-features-changed";

let memoryCache = null;
let inflight = null;

export const normalizeAppraisalFeatures = (raw) => {
  if (!raw || typeof raw !== "object") return { ...EMPTY_APPRAISAL_FEATURES };
  return {
    isRetrospective: Boolean(raw.isRetrospective ?? raw.IsRetrospective),
    isFeedback: Boolean(raw.isFeedback ?? raw.IsFeedback),
    isBehaviorGroup: Boolean(raw.isBehaviorGroup ?? raw.IsBehaviorGroup),
    isKpi: Boolean(raw.isKpi ?? raw.IsKpi),
    isDevelopment: Boolean(raw.isDevelopment ?? raw.IsDevelopment),
    isOneToOne: Boolean(raw.isOneToOne ?? raw.IsOneToOne),
    isOneToOneConversation: Boolean(raw.isOneToOneConversation ?? raw.IsOneToOneConversation),
    isPayIncrease: Boolean(raw.isPayIncrease ?? raw.IsPayIncrease),
    isLms: Boolean(raw.isLms ?? raw.IsLms),
  };
};

const readSessionCache = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeAppraisalFeatures(JSON.parse(raw));
  } catch (_) {
    return null;
  }
};

const writeSessionCache = (features) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(features));
  } catch (_) {
    /* ignore */
  }
};

export const clearAppraisalFeaturesCache = () => {
  memoryCache = null;
  inflight = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
};

export const notifyAppraisalFeaturesChanged = (features) => {
  const normalized = normalizeAppraisalFeatures(features);
  memoryCache = normalized;
  writeSessionCache(normalized);
  try {
    window.dispatchEvent(new CustomEvent(APPRAISAL_FEATURES_CHANGED_EVENT, { detail: normalized }));
  } catch (_) {
    /* ignore */
  }
};

export const fetchAppraisalFeatures = async ({ force = false } = {}) => {
  if (!force && memoryCache) return memoryCache;
  if (!force && inflight) return inflight;

  inflight = getAppraisalFeatureFlags()
    .then((data) => {
      const normalized = normalizeAppraisalFeatures(data);
      memoryCache = normalized;
      writeSessionCache(normalized);
      return normalized;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

/** Map optional appraisal routes → company feature flag key. */
export const APPRAISAL_ROUTE_FEATURE_FLAGS = {
  "/appraisal/objectives": "isKpi",
  "/appraisal/retrospectives": "isRetrospective",
  "/appraisal/lms": "isLms",
  "/appraisal/team-goals": "isKpi",
  "/appraisal/goals": "isKpi",
  "/appraisal/team-pdp": "isDevelopment",
  "/appraisal/pdp": "isDevelopment",
  "/appraisal/one-to-one": "isOneToOne",
  "/appraisal/team-one-to-one": "isOneToOne",
  "/appraisal/conversations": "isOneToOneConversation",
  "/appraisal/team-conversations": "isOneToOneConversation",
};

export const getFlagForAppraisalPath = (pathname) => {
  if (!pathname) return null;
  if (pathname.startsWith("/appraisal/goals")) return "isKpi";
  if (pathname.startsWith("/appraisal/pdp")) return "isDevelopment";
  if (pathname.startsWith("/appraisal/one-to-one")) return "isOneToOne";
  if (pathname.startsWith("/appraisal/conversations")) return "isOneToOneConversation";
  return APPRAISAL_ROUTE_FEATURE_FLAGS[pathname] || null;
};

export const isAppraisalFeatureEnabled = (features, flagKey) => {
  if (!flagKey) return true;
  if (!features) return false;
  return Boolean(features[flagKey]);
};

export const useAppraisalFeatures = () => {
  const [features, setFeatures] = useState(() => memoryCache || readSessionCache());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async (force = true) => {
    setLoading(true);
    setError("");
    try {
      const next = await fetchAppraisalFeatures({ force });
      setFeatures(next);
      return next;
    } catch (e) {
      setError(e?.message || "Failed to load appraisal features");
      setFeatures((prev) => prev || { ...EMPTY_APPRAISAL_FEATURES });
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always refetch from API so Sara/Mike see flags enabled by Admin in Settings.
    refresh(true);
    const onChanged = (e) => {
      if (e?.detail) setFeatures(normalizeAppraisalFeatures(e.detail));
      else refresh(true);
    };
    const onFocus = () => refresh(true);
    window.addEventListener(APPRAISAL_FEATURES_CHANGED_EVENT, onChanged);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener(APPRAISAL_FEATURES_CHANGED_EVENT, onChanged);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  return { features: features || EMPTY_APPRAISAL_FEATURES, loading, error, refresh };
};
